/**
 * Arabic catalog — the right-to-left (RTL) example locale.
 *
 * Selecting it drives `document.documentElement.dir = 'rtl'` (see the locale
 * store + RTL_LANGUAGES), so the whole shell mirrors. Declared
 * `: TranslationKeys`, so it is held to exact structural parity with the French
 * source at compile time. Strings outside the externalised tranche fall back to
 * French at runtime.
 */

import type { TranslationKeys } from './fr';

const ar: TranslationKeys = {
  common: {
    save: 'حفظ',
    cancel: 'إلغاء',
    delete: 'حذف',
    create: 'إنشاء',
    edit: 'تعديل',
    search: 'بحث',
    loading: 'جارٍ التحميل…',
    confirm: 'تأكيد',
    close: 'إغلاق',
    back: 'رجوع',
    next: 'التالي',
    previous: 'السابق',
    add: 'إضافة',
    remove: 'إزالة',
    apply: 'تطبيق',
    reset: 'إعادة تعيين',
    refresh: 'تحديث',
    retry: 'إعادة المحاولة',
    duplicate: 'تكرار',
    export: 'تصدير',
    import: 'استيراد',
    copy: 'نسخ',
    download: 'تنزيل',
    upload: 'رفع',
    filter: 'تصفية',
    all: 'الكل',
    none: 'لا شيء',
    yes: 'نعم',
    no: 'لا',
    ok: 'موافق',
    actions: 'إجراءات',
    more: 'المزيد',
    settings: 'الإعدادات',
    processing: 'جارٍ المعالجة…',
    readOnly: 'للقراءة فقط',
    empty: 'لا توجد عناصر',
  },

  nav: {
    brand: 'PubSocialNet',
    groups: {
      general: 'عام',
      creation: 'الإنشاء',
      prospection: 'التنقيب',
      ai: 'الذكاء الاصطناعي',
      system: 'النظام',
    },
    items: {
      dashboard: 'لوحة التحكم',
      content: 'المحتوى',
      calendar: 'التقويم',
      publishing: 'النشر',
      contentQueue: 'قائمة المحتوى',
      bulkSchedule: 'الجدولة الجماعية',
      platforms: 'المنصّات',
      ads: 'الإعلانات',
      mediaLibrary: 'مكتبة الوسائط',
      analytics: 'التحليلات',
      design: 'التصميم',
      workflows: 'سير العمل',
      crm: 'إدارة العملاء والعملاء المحتملين',
      discovery: 'الاستكشاف',
      prospectionRecommend: 'التوصيات',
      prospectionCalendar: 'تقويم التنقيب',
      accountIntelligence: 'ذكاء الحساب',
      carouselEditor: 'محرر العرض الدوار',
      richContentStudio: 'استوديو المحتوى الغني',
      prospect: 'قاعدة B2B',
      marketplace: 'السوق',
      social: 'عملاء اجتماعيون',
      outreach: 'التواصل عبر البريد',
      inbox: 'صندوق الوارد الاجتماعي',
      community: 'المجتمع',
      listening: 'الإنصات الاجتماعي',
      bio: 'رابط في النبذة',
      projects: 'المشاريع',
      aiTeam: 'فريق الذكاء الاصطناعي',
      aiOffice: 'مكتب الذكاء الاصطناعي',
      aiTeams: 'فرق الذكاء الاصطناعي',
      aiDeliverables: 'مخرجات الذكاء الاصطناعي',
      aiSchedules: 'جداول الذكاء الاصطناعي',
      aiControl: 'التحكم بالذكاء الاصطناعي',
      aiAuditTrail: 'سجل تدقيق الذكاء الاصطناعي',
      staffQuestions: 'أسئلة الوكلاء',
      grants: 'طلبات الصلاحيات',
      superadminFleet: 'أسطول المنصة',
      orchestration: 'تنسيق الذكاء الاصطناعي',
      aiAssistant: 'مساعد الذكاء الاصطناعي',
      contentStudio: 'استوديو المحتوى',
      ghostwriter: 'كاتب الظل بالذكاء الاصطناعي',
      repurpose: 'إعادة الاستخدام',
      hookLibrary: 'مكتبة الجُمَل الافتتاحية',
      brandVoice: 'صوت العلامة التجارية',
      abCopy: 'اختبارات A/B للنصوص',
      suggestions: 'اقتراحات الذكاء الاصطناعي',
      personalization: 'التخصيص',
      communityAi: 'مجتمع الذكاء الاصطناعي',
      ragDocuments: 'مستندات RAG',
      memory: 'ذاكرة الذكاء الاصطناعي',
      approvals: 'الموافقات',
      aiActivity: 'نشاط الذكاء الاصطناعي',
      settings: 'الإعدادات',
    },
    header: {
      openMenu: 'فتح القائمة',
      closeMenu: 'إغلاق القائمة',
      toggleSidebar: 'طيّ الشريط الجانبي أو فرده',
      breadcrumbRoot: 'PubSocialNet',
      breadcrumbAria: 'مسار التنقّل',
      searchPlaceholder: 'بحث…',
      openCommandPalette: 'فتح لوحة الأوامر',
      quickActions: 'إجراءات سريعة',
      create: 'إنشاء',
      createItem: 'إنشاء عنصر',
      shortcuts: 'اختصارات لوحة المفاتيح',
      showShortcuts: 'عرض اختصارات لوحة المفاتيح',
    },
    quickCreate: {
      newContent: 'محتوى جديد',
      newContact: 'جهة اتصال جديدة',
      newCompany: 'شركة جديدة',
      newCampaign: 'حملة جديدة',
      newProject: 'مشروع جديد',
      newDesign: 'تصميم جديد',
    },
    quickAccess: {
      search: 'بحث (⌘K)',
      newContent: 'محتوى جديد',
      newContact: 'جهة اتصال جديدة',
      newDeal: 'صفقة جديدة',
      inbox: 'صندوق الوارد الاجتماعي',
      assistant: 'مساعد الذكاء الاصطناعي',
    },
    user: {
      fallbackName: 'مستخدم',
      connectedAccount: 'حساب متصل',
      settings: 'الإعدادات',
      signout: 'تسجيل الخروج',
    },
    footer: {
      environment: 'بيئة التشغيل',
      whatsNew: 'ما الجديد',
      shortcuts: 'الاختصارات',
      privacy: 'الخصوصية',
      links: 'روابط التذييل',
      statusTitle: 'حالة الخدمة (يتم فحص ‎/health كل ٣٠ ثانية)',
      operational: 'يعمل',
      unavailable: 'الخدمة غير متاحة',
      checking: 'جارٍ التحقق…',
    },
    primaryNav: 'التنقّل الرئيسي',
  },

  settings: {
    title: 'الإعدادات',
    subtitle:
      'أرصدة الذكاء الاصطناعي والفوترة، وسجل الوكلاء والنماذج، والفريق، والملف الشخصي، والاتصالات الاجتماعية.',
    navAria: 'تنقّل الإعدادات',
    tabs: {
      credits: 'الأرصدة والفوترة',
      subscription: 'الاشتراك',
      referral: 'الإحالة',
      aiRegistry: 'سجل الذكاء الاصطناعي',
      governance: 'حوكمة الذكاء الاصطناعي',
      connectors: 'الموصّلات',
      developers: 'واجهة المطوّرين',
      integrations: 'التكاملات',
      connections: 'الحسابات المتصلة',
      team: 'الفريق',
      profile: 'الملف الشخصي',
    },
  },

  profile: {
    tabsAria: 'أقسام الملف الشخصي',
    tabProfile: 'الملف الشخصي',
    tabConnections: 'الاتصالات',
    emailVerified: 'تم التحقق من البريد الإلكتروني',
    emailUnverified: 'لم يتم التحقق من البريد الإلكتروني',
    noRole: 'لا يوجد دور',
    stats: {
      roles: 'الأدوار',
      session: 'الجلسة',
      expiresAt: 'تنتهي في',
    },
    account: {
      title: 'الحساب',
      name: 'الاسم',
      email: 'البريد الإلكتروني',
      username: 'المعرّف',
      tenant: 'المستأجر (tenant)',
      userId: 'معرّف المستخدم',
      roles: 'الأدوار',
    },
    prefs: {
      title: 'التفضيلات',
      language: 'اللغة',
      languageHint: 'يتم تذكّر التفضيل على هذا الجهاز.',
      theme: 'السمة',
      themeHint: '«النظام» يتبع إعداد الفاتح/الداكن في المتصفح.',
      themeSystem: 'النظام',
      themeLight: 'فاتح',
      themeDark: 'داكن',
      appearance: 'المظهر',
      appearanceHint: 'تبديل الفاتح/الداكن ولون التمييز — متاح أيضًا من الرأس.',
    },
  },

  platforms: {
    connections: {
      needsReconnectBadge: 'إعادة الربط مطلوبة',
      needsReconnectHint:
        'تعذّر على الخادم قراءة الرمز المخزَّن — لم يعد بإمكان هذا الربط النشر. أعد ربط الحساب لإصلاح ذلك.',
      reconnectAction: 'إعادة الربط',
    },
  },
};

export default ar;
